/**
 * Le fond du site : taches lumineuses et objets de verre, sur toute la page.
 *
 * Avant, chaque section portait son propre fond et son propre decor. Resultat :
 * des coutures visibles a chaque changement de section, et un hero qui semblait
 * decoupe du reste. Maintenant il y a UNE surface, celle du theme, et UNE
 * couche d'atmosphere par-dessus, fixe dans la fenetre.
 *
 * Fixe et non pas absolue : la couche reste en place pendant que le contenu
 * defile devant elle. C'est ce decalage qui donne la profondeur, et ca ne coute
 * que la taille de la fenetre a repeindre au lieu de la hauteur du document.
 *
 * Les anneaux et la sphere derivent en plus au defilement (--par, pose par
 * useDefilementPage), pour que le fond ne soit pas une image collee.
 */

import { useRef } from "react";
import { GlassDecor, DecorStyles } from "@/components/decor/GlassDecor";
import { useDefilementPage } from "@/lib/parallaxe";

const ACCENT_SOURDE = "hsl(217 95% 39%)";

interface Tache {
  classe: string;
  style: React.CSSProperties;
}

const TACHES: Tache[] = [
  {
    classe: "fond-derive",
    style: {
      top: "-18%",
      right: "-6%",
      width: "58vw",
      height: "58vw",
      background: `radial-gradient(circle, ${ACCENT_SOURDE} 0%, transparent 62%)`,
      opacity: 0.3,
      filter: "blur(90px)",
    },
  },
  {
    classe: "fond-derive-2",
    style: {
      bottom: "-24%",
      left: "-12%",
      width: "46vw",
      height: "46vw",
      background: "radial-gradient(circle, hsl(228 70% 30%) 0%, transparent 62%)",
      opacity: 0.35,
      filter: "blur(100px)",
    },
  },
  {
    classe: "fond-derive-3",
    style: {
      top: "30%",
      left: "22%",
      width: "34vw",
      height: "34vw",
      background: "radial-gradient(circle, hsl(198 88% 34%) 0%, transparent 60%)",
      opacity: 0.18,
      filter: "blur(120px)",
    },
  },
  {
    classe: "fond-derive-4",
    style: {
      bottom: "-14%",
      right: "16%",
      width: "40vw",
      height: "40vw",
      background: `radial-gradient(circle, ${ACCENT_SOURDE} 0%, transparent 64%)`,
      opacity: 0.16,
      filter: "blur(110px)",
    },
  },
];

export function FondAnime() {
  const cadre = useRef<HTMLDivElement>(null);
  useDefilementPage(cadre);

  return (
    <div
      ref={cadre}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
    >
      <style dangerouslySetInnerHTML={{ __html: `
        /* Uniquement des translations, jamais de scale.
           Ces surfaces portent un filter: blur() de 90 a 120 px. Animer leur
           echelle oblige le navigateur a re-tramer la texture floutee a chaque
           image : le profil processeur montrait 2,4 s passees dans le rendu
           pour ce seul motif. Une translation, elle, est deplacee par le
           compositeur sans rien redessiner. L'opacite suit la meme regle. */
        @keyframes fondDerive {
          0%, 100% { transform: translate3d(0, 0, 0); opacity: 1; }
          50%      { transform: translate3d(-4%, 5%, 0); opacity: .78; }
        }
        @keyframes fondDerive2 {
          0%, 100% { transform: translate3d(0, 0, 0); opacity: .85; }
          50%      { transform: translate3d(5%, -4%, 0); opacity: 1; }
        }
        .fond-derive   { animation: fondDerive 26s ease-in-out infinite; }
        .fond-derive-2 { animation: fondDerive2 34s ease-in-out infinite; }
        .fond-derive-3 { animation: fondDerive 44s ease-in-out infinite reverse; }
        .fond-derive-4 { animation: fondDerive2 38s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .fond-derive, .fond-derive-2, .fond-derive-3, .fond-derive-4 { animation: none; }
        }
      ` }} />
      <DecorStyles />

      {TACHES.map((t) => (
        <div key={t.classe} className={`${t.classe} absolute`} style={t.style} />
      ))}

      {/* Les objets de verre. Le --par pose par useParallaxe les fait glisser
          au defilement : le fond respire au lieu d'etre une image collee. */}
      <div
        className="absolute inset-0"
        style={{ transform: "translate3d(0, calc(var(--par, 0) * -40px), 0)" }}
      >
        <GlassDecor
          forme="anneau"
          taille="26vw"
          opacite={0.24}
          duree={52}
          flou={1}
          style={{ right: "-5%", top: "6%" }}
        />
        <GlassDecor
          forme="sphere"
          taille="15vw"
          opacite={0.14}
          duree={64}
          retard={-18}
          flou={3}
          style={{ left: "-4%", bottom: "8%" }}
        />
      </div>
      <div
        className="absolute inset-0"
        style={{ transform: "translate3d(0, calc(var(--par, 0) * 26px), 0)" }}
      >
        <GlassDecor
          forme="anneau"
          taille="18vw"
          opacite={0.12}
          duree={72}
          retard={-30}
          flou={4}
          style={{ left: "18%", top: "52%" }}
        />
      </div>
    </div>
  );
}
